// PostgREST → i18n error mapping for the M10 admin CSV import flow.
//
// When the `import_deals_csv` RPC returns 23514 (PostgreSQL `check_violation`),
// PostgREST surfaces the underlying constraint name inside the error message,
// shaped like `new row for relation "deals" violates check constraint "<name>"`.
// The Zod schema already mirrors most of these constraints client-side, so
// reaching a 23514 means either (a) a CHECK we haven't mirrored yet, or
// (b) a race between Zod and a DB-side trigger / constraint update. Either
// way, the admin deserves a specific, actionable message — not a generic
// "One row violated a database constraint" banner.
//
// Constraint names below were verified against `pg_constraint` on ddm-dev
// (2026-05-11) — see ADR-022 §D4. Update the table whenever a deals CHECK is
// added or renamed in migrations.

const CHECK_CONSTRAINT_RE = /violates check constraint "([^"]+)"/;

const CONSTRAINT_TO_I18N_KEY: Record<string, string> = {
  deals_amount_paid_check: 'admin.import.errors.checkDetailed.amountPaidExceedsOriginal',
  deals_area_check: 'admin.import.errors.checkDetailed.areaRange',
  deals_asking_price_positive: 'admin.import.errors.checkDetailed.askingPricePositive',
  deals_bathrooms_check: 'admin.import.errors.checkDetailed.bathroomsRange',
  deals_bedrooms_check: 'admin.import.errors.checkDetailed.bedroomsRange',
  deals_description_min_length: 'admin.import.errors.checkDetailed.descriptionTooShort',
  deals_off_plan_required_fields: 'admin.import.errors.checkDetailed.offPlanMissingPrices',
  deals_title_length_check: 'admin.import.errors.checkDetailed.titleLength',
};

export type MappedPostgrestError = {
  /** PostgreSQL SQLSTATE if recognisable (e.g. '23514', '42501'). */
  sqlState?: string;
  /** Name of the violated CHECK constraint, when applicable. */
  constraint?: string;
  /** Fully-qualified i18n key the UI should resolve with `t()`. */
  i18nKey: string;
};

/**
 * Inspect an unknown error thrown by the Supabase JS client and return the
 * best i18n key to surface to the user. The mapping is intentionally narrow:
 * the fallback `admin.import.errors.generic` covers everything we cannot
 * categorise, so the UI never has to chase missing keys.
 */
export function mapPostgrestError(err: unknown): MappedPostgrestError {
  if (typeof err !== 'object' || err === null) {
    return { i18nKey: 'admin.import.errors.generic' };
  }
  const candidate = err as { code?: unknown; message?: unknown };
  const sqlState = typeof candidate.code === 'string' ? candidate.code : undefined;
  const message = typeof candidate.message === 'string' ? candidate.message : '';

  if (sqlState === '42501') {
    return { sqlState, i18nKey: 'admin.import.errors.rls' };
  }
  if (sqlState === '54000') {
    return { sqlState, i18nKey: 'admin.import.errors.tooManyRowsRpc' };
  }
  if (sqlState === '22023') {
    return { sqlState, i18nKey: 'admin.import.errors.emptyRpc' };
  }
  if (sqlState === '23514') {
    const match = message.match(CHECK_CONSTRAINT_RE);
    const constraint = match?.[1];
    if (constraint && constraint in CONSTRAINT_TO_I18N_KEY) {
      return {
        sqlState,
        constraint,
        i18nKey: CONSTRAINT_TO_I18N_KEY[constraint],
      };
    }
    return {
      sqlState,
      constraint,
      i18nKey: 'admin.import.errors.check',
    };
  }

  return { sqlState, i18nKey: 'admin.import.errors.generic' };
}
